import type { BicValidationResult } from '@pages/payments/hooks/useBicValidation.ts';
import type { FC, PropsWithChildren } from 'react';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';

interface BicValidationContextValue {
	bicResults: Record<string, BicValidationResult | null>;
	setBicResult: (prefix: string, result: BicValidationResult | null) => void;
	resetAll: () => void;
}

const BicValidationContext = createContext<BicValidationContextValue | null>(null);

export const BicValidationProvider: FC<PropsWithChildren> = ({ children }) => {
	const [bicResults, setBicResults] = useState<
		Record<string, BicValidationResult | null>
	>({});

	const setBicResult = useCallback(
		(prefix: string, result: BicValidationResult | null) => {
			setBicResults((prev) => ({ ...prev, [prefix]: result }));
		},
		[],
	);

	const resetAll = useCallback(() => {
		setBicResults({});
	}, []);

	const value = useMemo(
		() => ({ bicResults, setBicResult, resetAll }),
		[bicResults, setBicResult, resetAll],
	);

	return (
		<BicValidationContext.Provider value={value}>
			{children}
		</BicValidationContext.Provider>
	);
};

export const useBicValidationContext = () => useContext(BicValidationContext);

export const isBicPopulatedField = (
	fieldKey: string,
	bicResults: Record<string, unknown>,
): boolean => {
	for (const prefix of Object.keys(bicResults)) {
		if (fieldKey.startsWith(`${prefix}.`) && bicResults[prefix]) {
			return true;
		}
	}
	return false;
};
